<template>
  <div ref="root" class="p2p-post-grid">
    <slot :cell="cell" />
  </div>
</template>

<script>
// Post cards in rows that always fill the width. As many columns as fit (each card at least `min`
// pixels wide, at most `max` per row), rows balanced to hold similar numbers of cards, and a shorter
// last row shares the whole width, so a lone card spans the row. Twelve tracks divide evenly for
// one to four cards per row.
const TRACKS = 12;
// A card at least this wide shows its picture beside the words instead of above them.
export const WIDE_CARD = 540;

export function rowSizes(count, columns) {
  if (count <= 0) return [];
  const rows = Math.ceil(count / Math.max(1, Math.min(columns, count)));
  const base = Math.floor(count / rows);
  const extra = count % rows;
  return Array.from({ length: rows }, (_, row) => base + (row < extra ? 1 : 0));
}

export default {
  name: 'PostGrid',
  props: {
    count: { type: Number, required: true },
    min: { type: Number, default: 300 },
    max: { type: Number, default: 4 },
    gap: { type: Number, default: 16 }
  },
  data: () => ({ width: 0, observer: null, frame: 0 }),
  computed: {
    columns() {
      return Math.max(1, Math.min(this.max, 4, Math.floor((this.width + this.gap) / (this.min + this.gap))));
    },
    cells() {
      const cells = [];
      rowSizes(this.count, this.columns).forEach(size => {
        const width = (this.width - this.gap * (size - 1)) / size;
        for (let i = 0; i < size; i += 1) cells.push({ style: { gridColumn: 'span ' + TRACKS / size }, wide: width >= WIDE_CARD });
      });
      return cells;
    }
  },
  mounted() {
    this.measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', this.measure);
      return;
    }
    this.observer = new ResizeObserver(() => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.measure);
    });
    this.observer.observe(this.$refs.root);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.frame);
    if (this.observer) this.observer.disconnect();
    else window.removeEventListener('resize', this.measure);
  },
  methods: {
    measure() {
      const root = this.$refs.root;
      if (root && root.clientWidth !== this.width) this.width = root.clientWidth;
    },
    cell(index) { return this.cells[index] || { style: {}, wide: false }; }
  }
};
</script>

<style>
.p2p-post-grid{display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:16px;align-items:stretch}
.p2p-post-grid>*{grid-column:span 12;min-width:0}
/* A cell that stacks something above its post card (Approvals): the card takes the rest of the row height. */
.p2p-post-grid>.p2p-stack>.p2p-post{flex:1 1 auto}
</style>
